<template>
  <div class="advanced-settings" v-loading="loading">
    <section class="page-hero">
      <div>
        <el-tag size="small" type="info" effect="plain">管理员只读诊断</el-tag>
        <h1>高级设置</h1>
        <p>仅供 GPT 管理员核对工具权限、运行边界和服务状态；本页不修改密钥、模型、任务或运行参数。</p>
      </div>
      <div class="hero-actions">
        <el-button size="small" @click="goBasicSettings">返回系统设置</el-button>
        <el-button size="small" type="primary" icon="el-icon-refresh" :loading="loading" @click="refreshData">刷新状态</el-button>
      </div>
    </section>

    <el-alert
      title="只读安全边界：已移除模型切换、密钥输入、MCP启停、任意工具测试和重复配置入口。需要变更时由 GPT 管理员通过后台受控流程执行。"
      type="warning"
      :closable="false"
      show-icon
      class="boundary-alert"
    />

    <section class="summary-grid">
      <el-card v-for="card in summaryCards" :key="card.key" shadow="never" class="summary-card">
        <div class="summary-icon" :class="card.tone"><i :class="card.icon"></i></div>
        <div>
          <span>{{ card.label }}</span>
          <strong>{{ card.value }}</strong>
          <p>{{ card.desc }}</p>
        </div>
      </el-card>
    </section>

    <el-tabs v-model="activeTab" type="border-card" class="advanced-tabs">
      <el-tab-pane label="运行边界" name="boundary">
        <section class="role-grid">
          <el-card v-for="role in roleCards" :key="role.key" shadow="never" class="role-card">
            <div class="role-icon" :class="role.tone"><i :class="role.icon"></i></div>
            <div>
              <strong>{{ role.title }}</strong>
              <p>{{ role.desc }}</p>
              <span>{{ role.scope }}</span>
            </div>
          </el-card>
        </section>

        <el-row :gutter="16">
          <el-col :xs="24" :lg="12">
            <el-card shadow="never" class="panel-card">
              <div slot="header" class="panel-head">
                <div>
                  <strong>分析大脑</strong>
                  <p>区分当前 ChatGPT 会话与后台旧 AI 配置。</p>
                </div>
                <el-tag size="small" type="success">边界已明确</el-tag>
              </div>

              <div class="boundary-list">
                <div class="boundary-line">
                  <i class="el-icon-chat-dot-round blue"></i>
                  <div>
                    <strong>ChatGPT 对话模型</strong>
                    <span>由 ChatGPT 平台管理，不受本系统页面控制，也不会通过这里切换版本。</span>
                  </div>
                </div>
                <div class="boundary-line">
                  <i class="el-icon-cpu gray"></i>
                  <div>
                    <strong>后台 AI 接口</strong>
                    <span>不是当前主分析入口；旧版 GPT-5.5 切换按钮已移除，避免误认为会改变聊天模型。</span>
                  </div>
                </div>
                <div class="boundary-line">
                  <i class="el-icon-lock green"></i>
                  <div>
                    <strong>模型或密钥变更</strong>
                    <span>仅由 GPT 管理员核验真实调用链后，通过预览、确认、测试和回执流程处理。</span>
                  </div>
                </div>
              </div>
            </el-card>
          </el-col>

          <el-col :xs="24" :lg="12">
            <el-card shadow="never" class="panel-card">
              <div slot="header" class="panel-head">
                <div>
                  <strong>用户入口分工</strong>
                  <p>日常操作回到对应的轻量页面。</p>
                </div>
              </div>

              <div class="shortcut-grid">
                <button type="button" class="shortcut" @click="goTaskMonitor">
                  <i class="el-icon-timer"></i>
                  <strong>任务监控</strong>
                  <span>运行时间、范围和任务状态</span>
                </button>
                <button type="button" class="shortcut" @click="goRunRecords">
                  <i class="el-icon-finished"></i>
                  <strong>运行记录</strong>
                  <span>结果、通知和诊断编号</span>
                </button>
                <button type="button" class="shortcut" @click="goHistoryMonitor">
                  <i class="el-icon-monitor"></i>
                  <strong>历史库监控</strong>
                  <span>覆盖、队列和补库状态</span>
                </button>
                <button type="button" class="shortcut" @click="goBasicSettings">
                  <i class="el-icon-setting"></i>
                  <strong>系统设置</strong>
                  <span>通知通道与只读数据源状态</span>
                </button>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <el-card shadow="never" class="panel-card cleanup-card">
          <div slot="header" class="panel-head">
            <div>
              <strong>已完成的高级页精简</strong>
              <p>保留诊断价值，删除容易误操作或与真实运行脱节的功能。</p>
            </div>
          </div>
          <div class="cleanup-grid">
            <div v-for="item in cleanupItems" :key="item.title" class="cleanup-item">
              <i class="el-icon-circle-check"></i>
              <div><strong>{{ item.title }}</strong><span>{{ item.desc }}</span></div>
            </div>
          </div>
        </el-card>
      </el-tab-pane>

      <el-tab-pane label="工具权限" name="tools">
        <div class="permission-grid">
          <div v-for="item in permissionSummary" :key="item.key" class="permission-card" :class="item.tone">
            <span>{{ item.label }}</span>
            <strong>{{ item.value }}</strong>
            <p>{{ item.desc }}</p>
          </div>
        </div>

        <el-card shadow="never" class="panel-card">
          <div slot="header" class="panel-head tools-head">
            <div>
              <strong>后台工具目录</strong>
              <p>只展示安全摘要；不暴露密钥、原始载荷，也不提供直接测试或启停按钮。</p>
            </div>
            <el-input v-model.trim="toolKeyword" size="small" clearable prefix-icon="el-icon-search" placeholder="按名称、说明或分类搜索" />
          </div>

          <el-alert
            v-if="toolsLoadFailed"
            title="工具目录暂时不可用。不会影响已经注册的后台工具，GPT 管理员可通过后台工具面继续核验。"
            type="warning"
            :closable="false"
            show-icon
            class="inline-alert"
          />

          <el-table :data="filteredTools" size="small" stripe empty-text="暂无可展示的工具目录">
            <el-table-column label="工具" min-width="220">
              <template slot-scope="scope">
                <strong>{{ scope.row.name || '未命名工具' }}</strong>
                <div class="sub-text">{{ safeDescription(scope.row.description) }}</div>
              </template>
            </el-table-column>
            <el-table-column label="分类" width="125">
              <template slot-scope="scope">{{ categoryText(scope.row.category) }}</template>
            </el-table-column>
            <el-table-column label="权限" width="115">
              <template slot-scope="scope">
                <el-tag size="mini" :type="permissionTagType(scope.row.permission)">{{ permissionText(scope.row.permission) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="使用说明" min-width="250">
              <template slot-scope="scope">{{ permissionUsage(scope.row.permission) }}</template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-tab-pane>

      <el-tab-pane label="系统状态" name="status">
        <el-row :gutter="16">
          <el-col :xs="24" :lg="12">
            <el-card shadow="never" class="panel-card status-panel">
              <div slot="header" class="panel-head">
                <div>
                  <strong>服务状态</strong>
                  <p>只读汇总，不执行重启或配置写入。</p>
                </div>
              </div>
              <el-descriptions :column="1" border size="small">
                <el-descriptions-item label="后端服务">
                  <el-tag size="mini" :type="backendStatusType">{{ backendStatusText }}</el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="MCP工具目录">{{ mcpTools.length }} 项</el-descriptions-item>
                <el-descriptions-item label="MCP配置状态">{{ mcpEnabledText }}</el-descriptions-item>
                <el-descriptions-item label="数据源状态">
                  <el-tag size="mini" :type="dataSourceStatusType">{{ dataSourceStatusText }}</el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="通知主通道">{{ notificationChannelText }}</el-descriptions-item>
                <el-descriptions-item label="最近刷新">{{ lastRefresh || '-' }}</el-descriptions-item>
              </el-descriptions>
            </el-card>
          </el-col>

          <el-col :xs="24" :lg="12">
            <el-card shadow="never" class="panel-card status-panel">
              <div slot="header" class="panel-head">
                <div>
                  <strong>系统信息</strong>
                  <p>后端返回什么就展示什么，不再写死旧模型版本。</p>
                </div>
              </div>
              <el-descriptions :column="1" border size="small">
                <el-descriptions-item label="系统名称">{{ systemInfo.name || '小七运行监控台' }}</el-descriptions-item>
                <el-descriptions-item label="后端版本">{{ systemInfo.version || '未提供' }}</el-descriptions-item>
                <el-descriptions-item label="更新日期">{{ systemInfo.update_time || systemInfo.update_date || '未提供' }}</el-descriptions-item>
                <el-descriptions-item label="页面定位">管理员只读诊断</el-descriptions-item>
                <el-descriptions-item label="分析定位">GPT / 小七为唯一分析大脑</el-descriptions-item>
                <el-descriptions-item label="变更入口">GPT聊天中的受控后台流程</el-descriptions-item>
              </el-descriptions>
            </el-card>
          </el-col>
        </el-row>

        <el-card shadow="never" class="panel-card status-note">
          <div class="status-note-icon"><i class="el-icon-info"></i></div>
          <div>
            <strong>为什么这里没有“保存”和“切换”按钮？</strong>
            <p>高级参数需要同时核对真实代码、运行配置、权限边界和回滚条件。网页按钮无法保证这些门禁，因此页面只负责展示，实际变更交由 GPT 管理员按受控流程完成。</p>
          </div>
        </el-card>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import { dataSourceApi, systemSettingsApi } from '@/api'

export default {
  name: 'SystemSettingsAdvancedReadonly',
  data() {
    return {
      loading: false,
      activeTab: 'boundary',
      mcpTools: [],
      toolsLoadFailed: false,
      toolKeyword: '',
      config: {
        mcp_enabled: null,
        notification: {}
      },
      systemInfo: {},
      backendStatus: 'unknown',
      dataSourceStatus: 'unknown',
      lastRefresh: ''
    }
  },
  computed: {
    filteredTools() {
      const keyword = String(this.toolKeyword || '').trim().toLowerCase()
      if (!keyword) return this.mcpTools
      return this.mcpTools.filter(tool => [
        tool.name,
        tool.description,
        tool.category,
        this.categoryText(tool.category),
        this.permissionText(tool.permission)
      ].join(' ').toLowerCase().includes(keyword))
    },
    permissionCounts() {
      return this.mcpTools.reduce((acc, tool) => {
        const key = String(tool.permission || 'unknown')
        acc[key] = (acc[key] || 0) + 1
        return acc
      }, {})
    },
    protectedToolCount() {
      return (this.permissionCounts.diff_preview || 0) +
        (this.permissionCounts.confirm_write || 0) +
        (this.permissionCounts.append_only || 0) +
        (this.permissionCounts.forbidden || 0)
    },
    summaryCards() {
      return [
        {
          key: 'backend', label: '后端服务', value: this.backendStatusText,
          desc: '只读健康状态', icon: 'el-icon-connection',
          tone: this.backendStatus === 'online' ? 'green' : this.backendStatus === 'offline' ? 'red' : 'gray'
        },
        {
          key: 'tools', label: '工具目录', value: String(this.mcpTools.length),
          desc: '当前读取到的后台工具', icon: 'el-icon-s-operation', tone: 'blue'
        },
        {
          key: 'readonly', label: '只读工具', value: String(this.permissionCounts.read_only || 0),
          desc: '不会修改业务数据', icon: 'el-icon-view', tone: 'green'
        },
        {
          key: 'guarded', label: '受控工具', value: String(this.protectedToolCount),
          desc: '需预览、确认或禁止调用', icon: 'el-icon-lock', tone: 'orange'
        }
      ]
    },
    permissionSummary() {
      return [
        { key: 'read_only', label: '只读', value: String(this.permissionCounts.read_only || 0), desc: '直接读取，不修改数据', tone: 'green' },
        { key: 'diff_preview', label: '写入预览', value: String(this.permissionCounts.diff_preview || 0), desc: '只生成变更计划', tone: 'orange' },
        { key: 'confirm_write', label: '确认写入', value: String(this.permissionCounts.confirm_write || 0), desc: '需要明确确认才能执行', tone: 'orange' },
        { key: 'append_only', label: '追加留痕', value: String(this.permissionCounts.append_only || 0), desc: '仅新增记录，不覆盖历史', tone: 'blue' },
        { key: 'forbidden', label: '禁止', value: String(this.permissionCounts.forbidden || 0), desc: '不向 GPT 暴露执行能力', tone: 'red' }
      ]
    },
    roleCards() {
      return [
        { key: 'user', title: '用户前端', desc: '只看状态和结果', scope: '不接触密钥、底层日志、模型切换和高风险参数。', icon: 'el-icon-user', tone: 'blue' },
        { key: 'gpt', title: 'GPT 管理员', desc: '分析、排障和受控变更', scope: '先核验，再预览、确认、执行和回读。', icon: 'el-icon-chat-dot-round', tone: 'green' },
        { key: 'backend', title: '后台系统', desc: '数据、任务、通知和留痕', scope: '不替代 GPT 做股票分析和投资决策。', icon: 'el-icon-cpu', tone: 'gray' }
      ]
    },
    cleanupItems() {
      return [
        { title: '移除旧模型切换', desc: '不再展示或切换写死的 GPT-5.5 配置。' },
        { title: '移除数据源写入', desc: '不再输入 Token、数据范围或缓存参数。' },
        { title: '移除 MCP 启停', desc: '避免网页误关后台工具能力。' },
        { title: '移除任意工具测试', desc: '不再让页面直接拼参数调用后台工具。' },
        { title: '移除重复通知配置', desc: '日常通知设置统一回到轻量系统设置页。' },
        { title: '保留只读诊断', desc: '工具权限、系统边界和服务状态仍可集中查看。' }
      ]
    },
    backendStatusText() {
      return { online: '在线', offline: '离线', unknown: '待检查' }[this.backendStatus] || '待检查'
    },
    backendStatusType() {
      return this.backendStatus === 'online' ? 'success' : this.backendStatus === 'offline' ? 'danger' : 'info'
    },
    dataSourceStatusText() {
      return { normal: '正常', abnormal: '异常', unavailable: '暂不可用', unknown: '待检查' }[this.dataSourceStatus] || '待检查'
    },
    dataSourceStatusType() {
      return this.dataSourceStatus === 'normal' ? 'success' : this.dataSourceStatus === 'abnormal' ? 'danger' : this.dataSourceStatus === 'unavailable' ? 'warning' : 'info'
    },
    mcpEnabledText() {
      if (this.config.mcp_enabled === true) return '已启用（只读展示）'
      if (this.config.mcp_enabled === false) return '未启用'
      return '状态未提供'
    },
    notificationChannelText() {
      const notification = this.config.notification || {}
      const channel = String(notification.default_channel || '').toLowerCase()
      if (channel === 'email') return '邮件'
      if (channel === 'wechat' || channel === 'feishu') return '飞书'
      if (notification.wechat_enabled === true || notification.feishu_enabled === true) return '飞书'
      return '状态未提供'
    }
  },
  mounted() {
    this.refreshData()
  },
  methods: {
    unwrapResponse(response) {
      return (response && (response.machine_payload || response.data || response.payload)) || response || {}
    },
    async refreshData() {
      this.loading = true
      const results = await Promise.allSettled([
        systemSettingsApi.getMcpTools(),
        systemSettingsApi.getConfig(),
        systemSettingsApi.getSystemInfo(),
        systemSettingsApi.healthCheck(),
        dataSourceApi.check()
      ])

      this.applyToolsResult(results[0])
      this.applyConfigResult(results[1])
      this.applySystemInfoResult(results[2])
      this.backendStatus = results[3].status === 'fulfilled' ? 'online' : 'offline'
      this.applyDataSourceResult(results[4])
      this.lastRefresh = this.nowText()
      this.loading = false
    },
    applyToolsResult(result) {
      if (!result || result.status !== 'fulfilled') {
        this.mcpTools = []
        this.toolsLoadFailed = true
        return
      }
      const payload = this.unwrapResponse(result.value)
      const tools = Array.isArray(payload) ? payload : (payload.tools || payload.items || payload.data || [])
      this.mcpTools = Array.isArray(tools) ? tools : []
      this.toolsLoadFailed = false
    },
    applyConfigResult(result) {
      if (!result || result.status !== 'fulfilled') return
      const payload = this.unwrapResponse(result.value)
      const config = payload.config && typeof payload.config === 'object' ? payload.config : payload
      this.config = {
        mcp_enabled: config.mcp_enabled,
        notification: config.notification && typeof config.notification === 'object' ? config.notification : {}
      }
    },
    applySystemInfoResult(result) {
      if (!result || result.status !== 'fulfilled') return
      const payload = this.unwrapResponse(result.value)
      this.systemInfo = payload.system && typeof payload.system === 'object' ? payload.system : payload
    },
    applyDataSourceResult(result) {
      if (!result || result.status !== 'fulfilled') {
        this.dataSourceStatus = 'unavailable'
        return
      }
      const payload = this.unwrapResponse(result.value)
      const value = String(payload.service_status || payload.health || payload.status || payload.token_status || '').toLowerCase()
      if (payload.success === false || ['error', 'failed', 'invalid', 'expired', 'unhealthy', 'abnormal'].includes(value)) {
        this.dataSourceStatus = 'abnormal'
      } else if (payload.success === true || ['ok', 'valid', 'healthy', 'normal', 'available', 'success'].includes(value)) {
        this.dataSourceStatus = 'normal'
      } else {
        this.dataSourceStatus = 'unknown'
      }
    },
    permissionText(permission) {
      return {
        read_only: '只读',
        diff_preview: '写入预览',
        confirm_write: '确认写入',
        append_only: '追加留痕',
        forbidden: '禁止'
      }[permission] || '未分类'
    },
    permissionTagType(permission) {
      return {
        read_only: 'success',
        diff_preview: 'warning',
        confirm_write: 'warning',
        append_only: 'info',
        forbidden: 'danger'
      }[permission] || 'info'
    },
    permissionUsage(permission) {
      return {
        read_only: '可直接读取；不得产生业务写入。',
        diff_preview: '只生成预览，不能直接落库或执行任务。',
        confirm_write: '必须有明确授权和有效确认材料。',
        append_only: '只能追加留痕，不覆盖既有记录。',
        forbidden: '不应通过 GPT 或页面调用。'
      }[permission] || '由 GPT 管理员进一步核验。'
    },
    categoryText(category) {
      return {
        first_book: '第一本账',
        stock: '股票数据',
        conclusion: '分析结论',
        task: '任务',
        notification: '通知',
        log: '日志',
        system: '系统',
        hddb: '历史库',
        general: '通用'
      }[category] || category || '未分类'
    },
    safeDescription(value) {
      const text = String(value || '').trim()
      if (!text) return '暂无说明'
      const lower = text.toLowerCase()
      const blocked = ['token=', 'authorization', 'secret', 'password', '/opt/', 'database_url']
      if (blocked.some(item => lower.includes(item))) return '技术说明已隐藏，由 GPT 管理员后台核验。'
      return text.slice(0, 160)
    },
    nowText() {
      const date = new Date()
      const parts = new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      }).formatToParts(date).reduce((acc, item) => {
        acc[item.type] = item.value
        return acc
      }, {})
      return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`
    },
    goBasicSettings() {
      this.$router.push('/system-settings')
    },
    goTaskMonitor() {
      this.$router.push('/scheduled-tasks')
    },
    goRunRecords() {
      this.$router.push('/scheduled-task-runs')
    },
    goHistoryMonitor() {
      this.$router.push('/history-monitor')
    }
  }
}
</script>

<style scoped>
.advanced-settings { max-width: 1320px; margin: 0 auto; }
.page-hero { min-height: 108px; display: flex; align-items: center; justify-content: space-between; gap: 24px; padding: 12px 4px 18px; border-bottom: 1px solid #e3ebf5; }
.page-hero h1 { margin: 10px 0 6px; color: #0f172a; font-size: 29px; }
.page-hero p { margin: 0; max-width: 850px; color: #64748b; line-height: 1.7; }
.hero-actions { display: flex; gap: 10px; flex-shrink: 0; }
.boundary-alert { margin: 16px 0; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin-bottom: 16px; }
.summary-card { border-radius: 15px; }
.summary-card /deep/ .el-card__body { display: flex; align-items: center; gap: 14px; padding: 17px 18px; }
.summary-icon { width: 44px; height: 44px; border-radius: 14px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 20px; background: #94a3b8; flex-shrink: 0; }
.summary-icon.blue { background: #2563eb; } .summary-icon.green { background: #16a34a; } .summary-icon.orange { background: #f59e0b; } .summary-icon.red { background: #ef4444; } .summary-icon.gray { background: #94a3b8; }
.summary-card span { color: #64748b; font-size: 12px; }
.summary-card strong { display: block; margin: 3px 0; color: #0f172a; font-size: 22px; }
.summary-card p { margin: 0; color: #94a3b8; font-size: 12px; }
.advanced-tabs { border-radius: 15px; overflow: hidden; }
.role-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; margin-bottom: 16px; }
.role-card { border-radius: 14px; }
.role-card /deep/ .el-card__body { display: grid; grid-template-columns: 48px 1fr; gap: 14px; align-items: start; padding: 18px; }
.role-icon { width: 46px; height: 46px; border-radius: 15px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 21px; background: #94a3b8; }
.role-icon.blue { background: #2563eb; } .role-icon.green { background: #16a34a; } .role-icon.gray { background: #64748b; }
.role-card strong { color: #0f172a; font-size: 15px; }
.role-card p { margin: 5px 0; color: #334155; font-size: 13px; }
.role-card span { color: #7f8ea3; font-size: 12px; line-height: 1.6; }
.panel-card { margin-bottom: 16px; border-radius: 14px; }
.panel-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; }
.panel-head strong { color: #0f172a; font-size: 15px; }
.panel-head p { margin: 4px 0 0; color: #7f8ea3; font-size: 12px; }
.boundary-list { display: flex; flex-direction: column; gap: 15px; }
.boundary-line { display: grid; grid-template-columns: 30px 1fr; gap: 10px; align-items: start; }
.boundary-line i { font-size: 20px; margin-top: 2px; }
.boundary-line i.blue { color: #2563eb; } .boundary-line i.green { color: #16a34a; } .boundary-line i.gray { color: #64748b; }
.boundary-line strong, .boundary-line span { display: block; }
.boundary-line strong { color: #334155; }
.boundary-line span { margin-top: 4px; color: #7f8ea3; font-size: 12px; line-height: 1.6; }
.shortcut-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.shortcut { border: 1px solid #dfe7f2; border-radius: 13px; background: #f8fbff; padding: 14px; text-align: left; cursor: pointer; font: inherit; transition: all .2s; }
.shortcut:hover { border-color: #9fc1f5; transform: translateY(-1px); box-shadow: 0 8px 18px rgba(37, 99, 235, .08); }
.shortcut i { color: #2563eb; font-size: 19px; }
.shortcut strong, .shortcut span { display: block; }
.shortcut strong { margin-top: 8px; color: #0f172a; }
.shortcut span { margin-top: 4px; color: #7f8ea3; font-size: 12px; }
.cleanup-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
.cleanup-item { display: grid; grid-template-columns: 24px 1fr; gap: 9px; padding: 13px; border-radius: 12px; background: #f6f9fd; }
.cleanup-item i { color: #16a34a; font-size: 18px; }
.cleanup-item strong, .cleanup-item span { display: block; }
.cleanup-item strong { color: #334155; font-size: 13px; }
.cleanup-item span { margin-top: 4px; color: #7f8ea3; font-size: 12px; line-height: 1.55; }
.permission-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px; margin-bottom: 16px; }
.permission-card { padding: 15px; border-radius: 13px; background: #f5f8fc; border: 1px solid #e3eaf3; }
.permission-card.green { background: #f2fbf5; border-color: #cfead8; } .permission-card.orange { background: #fff9ee; border-color: #f3dfb8; } .permission-card.blue { background: #f1f6ff; border-color: #cfdff8; } .permission-card.red { background: #fff4f4; border-color: #f0cece; }
.permission-card span { color: #64748b; font-size: 12px; }
.permission-card strong { display: block; margin: 5px 0; color: #0f172a; font-size: 23px; }
.permission-card p { margin: 0; color: #94a3b8; font-size: 12px; line-height: 1.5; }
.tools-head .el-input { width: 300px; }
.inline-alert { margin-bottom: 14px; }
.sub-text { margin-top: 4px; color: #94a3b8; font-size: 12px; line-height: 1.5; }
.status-panel { min-height: 360px; }
.status-note /deep/ .el-card__body { display: grid; grid-template-columns: 48px 1fr; gap: 14px; align-items: start; }
.status-note-icon { width: 46px; height: 46px; border-radius: 15px; display: flex; align-items: center; justify-content: center; background: #e8f1ff; color: #2563eb; font-size: 21px; }
.status-note strong { color: #0f172a; }
.status-note p { margin: 6px 0 0; color: #64748b; line-height: 1.7; }
@media (max-width: 1100px) { .summary-grid { grid-template-columns: repeat(2, 1fr); } .permission-grid { grid-template-columns: repeat(3, 1fr); } .cleanup-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 800px) { .page-hero, .panel-head { align-items: flex-start; flex-direction: column; } .hero-actions { width: 100%; } .role-grid, .summary-grid, .permission-grid, .cleanup-grid, .shortcut-grid { grid-template-columns: 1fr; } .tools-head .el-input { width: 100%; } }
</style>
